// Metronome — a Web Audio click slaved to the single engine clock. It owns no timer:
// `main.ts` calls `clicksToSound` each RAF frame and sounds the returned accents, and the
// AudioContext is the audio sink, not a timebase. The beat grid (`Song.beats`, downbeats
// via `Song.bars`) comes from the parser.

/** A beat crossed this frame: its index in `beats`, accented if it's a downbeat. */
interface DueBeat {
  index: number;
  accent: boolean;
}

const EPS = 1e-6;

/** Peak gain for a click: the accent (downbeat) is louder, scaled by `volume`
 *  (clamped to [0,1]). Never returns 0 — the click's exponential ramp needs a
 *  strictly positive target. Pure. */
export function clickPeak(accent: boolean, volume: number): number {
  const base = accent ? 0.4 : 0.25;
  const v = Math.max(0, Math.min(1, volume));
  return Math.max(EPS, base * v);
}

/**
 * Indices of beats whose onset falls in the half-open interval `(prev, now]`, each
 * flagged `accent` when that beat is also a barline (downbeat). Half-open so a beat
 * landing exactly on a frame boundary fires once, never twice. Pure.
 */
export function beatsDue(prev: number, now: number, beats: number[], bars: number[]): DueBeat[] {
  const due: DueBeat[] = [];
  if (now <= prev) return due;
  // beats is ascending — binary-search the first beat strictly after `prev` so the
  // per-frame call (single-clock hot path) doesn't rescan the whole array each time.
  let lo = 0;
  let hi = beats.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((beats[mid] ?? 0) <= prev) lo = mid + 1;
    else hi = mid;
  }
  for (let i = lo; i < beats.length; i++) {
    const t = beats[i];
    if (t === undefined || t > now) break; // sorted → nothing later is in-window either
    due.push({ index: i, accent: bars.some((b) => Math.abs(b - t) < EPS) });
  }
  return due;
}

/** Which clock drives the click this frame. */
type ClickSource = "playhead" | "pulse" | "none";

/**
 * Pick the click's clock. The playhead is *not* real time — wait mode holds it at an
 * unplayed note, the coach loops and idle-pauses it, and every mode opens in a negative
 * lead-in before the grid's first beat — so slaving the click to it alone leaves an
 * enabled metronome silent exactly when you need the beat to play to. Follow the playhead
 * only while it actually sweeps the grid; free-run the practice pulse the rest of the
 * time, so "on" always means sounding. Silent only when off and in auto mode (the attract
 * reel is silent by design); the pulse clicks straight through the count-in pre-roll, which
 * is what keeps the beat unbroken across the drop-in. Pure.
 */
export function clickSource(s: {
  enabled: boolean;
  mode: string;
  playing: boolean;
  prevPlayhead: number;
  playhead: number;
}): ClickSource {
  if (!s.enabled || s.mode === "auto") return "none";
  const sweeping = s.playing && s.playhead > s.prevPlayhead && s.playhead >= 0;
  return sweeping ? "playhead" : "pulse";
}

/**
 * The accents to sound this frame (one `metronome.click(accent)` each) and the carried
 * `pulseJustClicked` latch. Merges the two clocks so an enabled click is *never* silent:
 *
 * - **Sweeping the grid** → sound the beats crossed in `(prevPlayhead, playhead]`
 *   (`beatsDue`), whose downbeat accents come from the song's real barlines. But if the
 *   swept span crosses *no* grid beat — an off-grid drill-window tail past the last beat,
 *   or a sparse grid — the phase-aligned free-run pulse fills in, so the sweep never falls
 *   silent where the grid happens to be empty. (This is the bug fixed here: following the
 *   playhead used to suppress the pulse outright, so a beatless tail went quiet until the
 *   next segment's notes reached the keyline.)
 * - **Not sweeping** (paused, lead-in, wait gate, coach loop/idle, a seek) → the free-run
 *   pulse carries the beat.
 * - **Off / auto** → nothing (the attract reel is silent by design).
 *
 * `pulseJustClicked` suppresses a grid beat the pulse already sounded a frame earlier, so
 * the pulse→playhead handoff never flams. `anchor` (music-seconds the playhead has swept
 * past the grid beat it just crossed, else null) is fed to `PracticePulse.anchor` so the
 * free-run pulse stays locked to the grid — without it the fill-in click drifts and lands
 * off the beat. Pure.
 */
export function clicksToSound(s: {
  enabled: boolean;
  mode: string;
  playing: boolean;
  prevPlayhead: number;
  playhead: number;
  beats: number[];
  bars: number[];
  /** Accents from `PracticePulse.advance` this frame (one per beat that came due). */
  pulseBeats: boolean[];
  /** The pulse sounded a beat on the previous frame. */
  pulseJustClicked: boolean;
}): { accents: boolean[]; pulseJustClicked: boolean; anchor: number | null } {
  const source = clickSource(s);
  if (source === "playhead") {
    const grid = beatsDue(s.prevPlayhead, s.playhead, s.beats, s.bars);
    if (grid.length > 0) {
      // Re-anchor the silent pulse to the beat we just crossed, so it stays on the grid for
      // the next beatless tail / seek instead of drifting.
      const last = grid[grid.length - 1];
      const anchor = last ? s.playhead - (s.beats[last.index] ?? s.playhead) : null;
      // A beat the pulse sounded a frame ago is the same beat (they're phase-aligned) —
      // let it stand rather than flam it.
      return {
        accents: s.pulseJustClicked ? [] : grid.map((d) => d.accent),
        pulseJustClicked: false,
        anchor,
      };
    }
    // Beatless span while sweeping: the grid-locked pulse keeps the beat unbroken, on-beat.
    return { accents: s.pulseBeats, pulseJustClicked: s.pulseBeats.length > 0, anchor: null };
  }
  if (source === "pulse") {
    return { accents: s.pulseBeats, pulseJustClicked: s.pulseBeats.length > 0, anchor: null };
  }
  return { accents: [], pulseJustClicked: false, anchor: null };
}

/** Lazily-created Web Audio click. One per app; toggled by `enabled`. */
export class Metronome {
  enabled = false;
  /** Click loudness, 0..1 (scales the blip's peak gain). */
  volume = 1;
  private ctx: AudioContext | null = null;

  /** AudioContext state for diagnostics: "uninitialized" until first use, then the
   *  context's own state ("running" / "suspended" / "closed"). A "suspended" state
   *  means the autoplay policy hasn't been satisfied yet (no user gesture / flag). */
  get audioState(): string {
    return this.ctx?.state ?? "uninitialized";
  }

  private acquire(): AudioContext | null {
    const Ctor =
      typeof AudioContext !== "undefined"
        ? AudioContext
        : (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null; // headless/unit context — no audio, never throw
    if (!this.ctx) this.ctx = new Ctor();
    return this.ctx;
  }

  /** Resume the AudioContext after a user gesture (autoplay policy). */
  resume(): void {
    void this.acquire()?.resume();
  }

  /** A short percussive blip; the accent (downbeat) is higher-pitched. */
  click(accent: boolean): void {
    const ctx = this.acquire();
    if (!ctx) return;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = accent ? 1600 : 1000;
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(clickPeak(accent, this.volume), t + 0.001);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.06);
  }
}
